export type Language = 'en' | 'id' | 'ko' | 'vn';

export const SUPPORTED_LANGUAGES: Language[] = ['en', 'id', 'ko', 'vn'];

/**
 * Pure JavaScript language manager that syncs with localStorage
 * and provides language state without React dependencies.
 */
class LanguageManager {
    private currentLanguage: Language = 'en';
    private listeners: Set<(lang: Language) => void> = new Set();

    constructor() {
        // Initialize from URL path first, then localStorage, then navigator.language
        const langFromUrl = this.getLanguageFromUrl();
        if (langFromUrl) {
            this.currentLanguage = langFromUrl;
            localStorage.setItem('language', langFromUrl);
        } else {
            const saved = localStorage.getItem('language');
            if (saved && SUPPORTED_LANGUAGES.includes(saved as Language)) {
                this.currentLanguage = saved as Language;
            } else {
                // Fallback to navigator.language
                const browserLang = this.detectBrowserLanguage();
                this.currentLanguage = browserLang;
            }
        }
    }

    /**
     * Detect language from URL path (e.g., /en/..., /id/..., /ko/..., /vn/...)
     */
    private getLanguageFromUrl(): Language | null {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const firstSegment = pathSegments[0];
        if (firstSegment && SUPPORTED_LANGUAGES.includes(firstSegment as Language)) {
            return firstSegment as Language;
        }
        return null;
    }

    /**
     * Detect language from browser's navigator.language
     */
    private detectBrowserLanguage(): Language {
        const browserLang = navigator.language.toLowerCase().split('-')[0];
        const langMap: Record<string, Language> = {
            'en': 'en',
            'id': 'id',
            'ko': 'ko',
            'vi': 'vn'  // Vietnamese uses 'vi' in navigator.language
        };
        return langMap[browserLang] || 'en';
    }

    /**
     * Update language from URL (called during navigation)
     * If no language in URL, uses navigator.language preference
     */
    syncFromUrl(): void {
        const langFromUrl = this.getLanguageFromUrl();
        if (langFromUrl) {
            // URL has language prefix - use it
            if (langFromUrl !== this.currentLanguage) {
                this.currentLanguage = langFromUrl;
                localStorage.setItem('language', langFromUrl);
                this.notifyListeners();
            }
        } else {
            // No language in URL - use browser preference (session-only, not saved)
            const browserLang = this.detectBrowserLanguage();
            if (browserLang !== this.currentLanguage) {
                this.currentLanguage = browserLang;
                // Don't save to localStorage - this is session preference from navigator
                this.notifyListeners();
            }
        }
    }

    /**
     * Get browser's preferred language from navigator.language
     */
    getBrowserLanguage(): Language {
        return this.detectBrowserLanguage();
    }

    /**
     * Get current language
     */
    getLanguage(): Language {
        return this.currentLanguage;
    }

    /**
     * Set language and notify all listeners
     */
    setLanguage(lang: Language): void {
        if (this.currentLanguage !== lang) {
            this.currentLanguage = lang;
            localStorage.setItem('language', lang);
            this.notifyListeners();
        }
    }

    /**
     * Subscribe to language changes
     * Returns unsubscribe function
     */
    subscribe(listener: (lang: Language) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.currentLanguage));
    }
}

// Single instance shared across the app
export const languageManager = new LanguageManager();